import { l as lazyInit, d as debounce, R as ResizeNotifier } from "./utilities-2Suih3Nm.js";
import { i as isBrowser, d as doc, a as isArr, g as getNodeList, s as setValue, b as isObj, c as isFnc, k as keepTime, e as isUnd, f as isStr, h as isNum, r as relativeValuesExecRgx, j as getRelativeValue, l as stringStartsWith, m as isNil, n as minValue, p as parseEase, q as parseNumber, u as unitsExecRgx, t as emptyString, v as registerTargets, x as getOriginalAnimatableValue, y as abs, z as floor, A as sqrt, B as round, C as max, w as waapi, o as onScroll } from "./scroll-xmwq2Kx4.js";
/**
 * Anime.js - text - ESM
 * @version v4.2.2
 * @license MIT
 * @copyright 2025 - Julian Garnier
 */
const segmenter = typeof Intl !== "undefined" && Intl.Segmenter;
const valueRgx = /\{value\}/g;
const indexRgx = /\{i\}/g;
const whiteSpaceGroupRgx = /(\s+)/;
const whiteSpaceRgx = /^\s+$/;
const lineType = "line";
const wordType = "word";
const charType = "char";
const dataLine = `data-line`;
let wordSegmenter = null;
let graphemeSegmenter = null;
let $splitTemplate = null;
const isSegmentWordLike = (seg) => {
  return seg.isWordLike || seg.segment === " " || // Consider spaces as words first, then handle them diffrently later
  isNum(+seg.segment);
};
const setAriaHidden = ($el) => $el.setAttribute("aria-hidden", "true");
const getAllTopLevelElements = ($el, type) => [.../** @type {*} */
$el.querySelectorAll(`[data-${type}]:not([data-${type}] [data-${type}])`)];
const debugColors = { line: "#00D672", word: "#FF4B4B", char: "#5A87FF" };
const filterEmptyElements = ($el) => {
  if (!$el.childElementCount && !$el.textContent.trim()) {
    const $parent = $el.parentElement;
    $el.remove();
    if ($parent) filterEmptyElements($parent);
  }
};
const filterLineElements = ($el, lineIndex, bin) => {
  const dataLineAttr = $el.getAttribute(dataLine);
  if (dataLineAttr !== null && +dataLineAttr !== lineIndex || $el.tagName === "BR") bin.add($el);
  let i = $el.childElementCount;
  while (i--) filterLineElements(
    /** @type {HTMLElement} */
    $el.children[i],
    lineIndex,
    bin
  );
  return bin;
};
const generateTemplate = (type, params = {}) => {
  let template = ``;
  const classString = isStr(params.class) ? ` class="${params.class}"` : "";
  const cloneType = setValue(params.clone, false);
  const wrapType = setValue(params.wrap, false);
  const overflow = wrapType ? wrapType === true ? "clip" : wrapType : cloneType ? "clip" : false;
  if (wrapType) template += `<span${overflow ? ` style="overflow:${overflow};"` : ""}>`;
  template += `<span${classString}${cloneType ? ` style="position:relative;"` : ""} data-${type}="{i}">`;
  if (cloneType) {
    const left = cloneType === "left" ? "-100%" : cloneType === "right" ? "100%" : "0";
    const top = cloneType === "top" ? "-100%" : cloneType === "bottom" ? "100%" : "0";
    template += `<span>{value}</span>`;
    template += `<span inert style="position:absolute;top:${top};left:${left};white-space:nowrap;">{value}</span>`;
  } else {
    template += `{value}`;
  }
  template += `</span>`;
  if (wrapType) template += `</span>`;
  return template;
};
const processHTMLTemplate = (htmlTemplate, store, node, $parentFragment, type, debug, lineIndex, wordIndex, charIndex) => {
  const isLine = type === lineType;
  const isChar = type === charType;
  const className = `_${type}_`;
  const template = isFnc(htmlTemplate) ? htmlTemplate(node) : htmlTemplate;
  const displayStyle = isLine ? "block" : "inline-block";
  $splitTemplate.innerHTML = template.replace(valueRgx, `<i class="${className}"></i>`).replace(indexRgx, `${isChar ? charIndex : isLine ? lineIndex : wordIndex}`);
  const $content = $splitTemplate.content;
  const $highestParent = (
    /** @type {HTMLElement} */
    $content.firstElementChild
  );
  const $split = (
    /** @type {HTMLElement} */
    $content.querySelector(`[data-${type}]`) || $highestParent
  );
  const $replacables = (
    /** @type {NodeListOf<HTMLElement>} */
    $content.querySelectorAll(`i.${className}`)
  );
  const replacablesLength = $replacables.length;
  if (replacablesLength) {
    $highestParent.style.display = displayStyle;
    $split.style.display = displayStyle;
    $split.setAttribute(dataLine, `${lineIndex}`);
    if (!isLine) {
      $split.setAttribute("data-word", `${wordIndex}`);
      if (isChar) $split.setAttribute("data-char", `${charIndex}`);
    }
    let i = replacablesLength;
    while (i--) {
      const $replace = $replacables[i];
      const $closestParent = $replace.parentElement;
      $closestParent.style.display = displayStyle;
      if (isLine) {
        $closestParent.innerHTML = /** @type {HTMLElement} */
        node.innerHTML;
      } else {
        $closestParent.replaceChild(node.cloneNode(true), $replace);
      }
    }
    store.push($split);
    $parentFragment.appendChild($content);
  } else {
  }
  if (debug) $highestParent.style.outline = `1px dotted ${debugColors[type]}`;
  return $highestParent;
};
class TextSplitter {
  /**
   * @param  {HTMLElement|NodeList|String|Array<HTMLElement>} target
   * @param  {TextSplitterParams} [parameters]
   */
  constructor(target, parameters = {}) {
    if (!wordSegmenter) wordSegmenter = segmenter ? new segmenter([], { granularity: wordType }) : {
      segment: (text) => {
        const segments = [];
        const words2 = text.split(whiteSpaceGroupRgx);
        for (let i = 0, l = words2.length; i < l; i++) {
          const segment = words2[i];
          segments.push({
            segment,
            isWordLike: !whiteSpaceRgx.test(segment)
            // Consider non-whitespace as word-like
          });
        }
        return segments;
      }
    };
    if (!graphemeSegmenter) graphemeSegmenter = segmenter ? new segmenter([], { granularity: "grapheme" }) : {
      segment: (text) => [...text].map((char) => ({ segment: char }))
    };
    if (!$splitTemplate && isBrowser) $splitTemplate = doc.createElement("template");
    const { words, chars, lines, accessible, includeSpaces, debug } = parameters;
    const $target = (
      /** @type {HTMLElement} */
      (target = isArr(target) ? target[0] : target) && /** @type {Node} */
      target.nodeType ? target : (getNodeList(target) || [])[0]
    );
    const lineParams = lines === true ? {} : lines;
    const wordParams = words === true || isUnd(words) ? {} : words;
    const charParams = chars === true ? {} : chars;
    this.debug = setValue(debug, false);
    this.includeSpaces = setValue(includeSpaces, false);
    this.accessible = setValue(accessible, true);
    this.linesOnly = lineParams && (!wordParams && !charParams);
    this.lineTemplate = isObj(lineParams) ? generateTemplate(
      lineType,
      /** @type {SplitTemplateParams} */
      lineParams
    ) : lineParams;
    this.wordTemplate = isObj(wordParams) || this.linesOnly ? generateTemplate(
      wordType,
      /** @type {SplitTemplateParams} */
      wordParams
    ) : wordParams;
    this.charTemplate = isObj(charParams) ? generateTemplate(
      charType,
      /** @type {SplitTemplateParams} */
      charParams
    ) : charParams;
    this.$target = $target;
    this.html = $target && $target.innerHTML;
    this.lines = [];
    this.words = [];
    this.chars = [];
    this.effects = [];
    this.effectsCleanups = [];
    this.cache = null;
    this.ready = false;
    this.width = 0;
    this.resizeTimeout = null;
    const handleSplit = () => this.html && (lineParams || wordParams || charParams) && this.split();
    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        const currentWidth = (
          /** @type {HTMLElement} */
          $target.offsetWidth
        );
        if (currentWidth === this.width) return;
        this.width = currentWidth;
        handleSplit();
      }, 150);
    });
    if (this.lineTemplate && !this.ready) {
      doc.fonts.ready.then(handleSplit);
    } else {
      handleSplit();
    }
    $target ? this.resizeObserver.observe($target) : void 0;
  }
  /**
   * @param  {(...args: any[]) => Tickable | (() => void)} effect
   * @return this
   */
  addEffect(effect) {
    if (!isFnc(effect)) return void 0;
    const refreshableEffect = keepTime(effect);
    this.effects.push(refreshableEffect);
    if (this.ready) this.effectsCleanups[this.effects.length - 1] = refreshableEffect(this);
    return this;
  }
  revert() {
    clearTimeout(this.resizeTimeout);
    this.lines.length = this.words.length = this.chars.length = 0;
    this.resizeObserver.disconnect();
    this.effectsCleanups.forEach((cleanup) => isFnc(cleanup) ? cleanup(this) : cleanup.revert && cleanup.revert());
    this.$target.innerHTML = this.html;
    return this;
  }
  /**
   * Recursively processes a node and its children
   * @param {Node} node
   */
  splitNode(node) {
    const wordTemplate = this.wordTemplate;
    const charTemplate = this.charTemplate;
    const includeSpaces = this.includeSpaces;
    const debug = this.debug;
    const nodeType = node.nodeType;
    if (nodeType === 3) {
      const nodeText = node.nodeValue;
      if (nodeText.trim()) {
        const tempWords = [];
        const words = this.words;
        const chars = this.chars;
        const wordSegments = wordSegmenter.segment(nodeText);
        const $wordsFragment = doc.createDocumentFragment();
        let prevSeg = null;
        for (const wordSegment of wordSegments) {
          const segment = wordSegment.segment;
          const isWordLike = isSegmentWordLike(wordSegment);
          if (!prevSeg || isWordLike && (prevSeg && isSegmentWordLike(prevSeg))) {
            tempWords.push(segment);
          } else {
            const lastWordIndex = tempWords.length - 1;
            const lastWord = tempWords[lastWordIndex];
            if (!lastWord.includes(" ") && !segment.includes(" ")) {
              tempWords[lastWordIndex] += segment;
            } else {
              tempWords.push(segment);
            }
          }
          prevSeg = wordSegment;
        }
        for (let i = 0, l = tempWords.length; i < l; i++) {
          const word = tempWords[i];
          if (!word.trim()) {
            if (i && includeSpaces) continue;
            $wordsFragment.appendChild(doc.createTextNode(word));
          } else {
            const nextWord = tempWords[i + 1];
            const hasWordFollowingSpace = includeSpaces && nextWord && !nextWord.trim();
            const wordToProcess = word;
            const charSegments = charTemplate ? graphemeSegmenter.segment(wordToProcess) : null;
            const $charsFragment = charTemplate ? doc.createDocumentFragment() : doc.createTextNode(hasWordFollowingSpace ? word + " " : word);
            if (charTemplate) {
              const charSegmentsArray = [...charSegments];
              for (let j = 0, jl = charSegmentsArray.length; j < jl; j++) {
                const charSegment = charSegmentsArray[j];
                const isLastChar = j === jl - 1;
                const charText = isLastChar && hasWordFollowingSpace ? charSegment.segment + " " : charSegment.segment;
                const $charNode = doc.createTextNode(charText);
                processHTMLTemplate(
                  charTemplate,
                  chars,
                  $charNode,
                  /** @type {DocumentFragment} */
                  $charsFragment,
                  charType,
                  debug,
                  -1,
                  words.length,
                  chars.length
                );
              }
            }
            if (wordTemplate) {
              processHTMLTemplate(wordTemplate, words, $charsFragment, $wordsFragment, wordType, debug, -1, words.length, chars.length);
            } else if (charTemplate) {
              $wordsFragment.appendChild($charsFragment);
            } else {
              $wordsFragment.appendChild(doc.createTextNode(word));
            }
            if (hasWordFollowingSpace) i++;
          }
        }
        node.parentNode.replaceChild($wordsFragment, node);
      }
    } else if (nodeType === 1) {
      const childNodes = (
        /** @type {Array<Node>} */
        [.../** @type {*} */
        node.childNodes]
      );
      for (let i = 0, l = childNodes.length; i < l; i++) this.splitNode(childNodes[i]);
    }
  }
  /**
   * @param {Boolean} clearCache
   * @return {this}
   */
  split(clearCache = false) {
    const $el = this.$target;
    const isCached = !!this.cache && !clearCache;
    const lineTemplate = this.lineTemplate;
    const wordTemplate = this.wordTemplate;
    const charTemplate = this.charTemplate;
    const fontsReady = doc.fonts.status !== "loading";
    const canSplitLines = lineTemplate && fontsReady;
    this.ready = !lineTemplate || fontsReady;
    if (canSplitLines || clearCache) {
      this.effectsCleanups.forEach((cleanup) => isFnc(cleanup) && cleanup(this));
    }
    if (!isCached) {
      if (clearCache) {
        $el.innerHTML = this.html;
        this.words.length = this.chars.length = 0;
      }
      this.splitNode($el);
      this.cache = $el.innerHTML;
    }
    if (canSplitLines) {
      if (isCached) $el.innerHTML = this.cache;
      this.lines.length = 0;
      if (wordTemplate) this.words = getAllTopLevelElements($el, wordType);
    }
    if (charTemplate && (canSplitLines || wordTemplate)) {
      this.chars = getAllTopLevelElements($el, charType);
    }
    const elementsArray = this.words.length ? this.words : this.chars;
    let y, linesCount = 0;
    for (let i = 0, l = elementsArray.length; i < l; i++) {
      const $el2 = elementsArray[i];
      const { top, height } = $el2.getBoundingClientRect();
      if (y && top - y > height * 0.5) linesCount++;
      $el2.setAttribute(dataLine, `${linesCount}`);
      const nested = $el2.querySelectorAll(`[${dataLine}]`);
      let c = nested.length;
      while (c--) nested[c].setAttribute(dataLine, `${linesCount}`);
      y = top;
    }
    if (canSplitLines) {
      const linesFragment = doc.createDocumentFragment();
      const parents = /* @__PURE__ */ new Set();
      const clones = [];
      for (let lineIndex = 0; lineIndex < linesCount + 1; lineIndex++) {
        const $clone = (
          /** @type {HTMLElement} */
          $el.cloneNode(true)
        );
        filterLineElements($clone, lineIndex, /* @__PURE__ */ new Set()).forEach(($el2) => {
          const $parent = $el2.parentElement;
          if ($parent) parents.add($parent);
          $el2.remove();
        });
        clones.push($clone);
      }
      parents.forEach(filterEmptyElements);
      for (let cloneIndex = 0, clonesLength = clones.length; cloneIndex < clonesLength; cloneIndex++) {
        processHTMLTemplate(lineTemplate, this.lines, clones[cloneIndex], linesFragment, lineType, this.debug, cloneIndex);
      }
      $el.innerHTML = "";
      $el.appendChild(linesFragment);
      if (wordTemplate) this.words = getAllTopLevelElements($el, wordType);
      if (charTemplate) this.chars = getAllTopLevelElements($el, charType);
    }
    if (this.linesOnly) {
      const words = this.words;
      let w = words.length;
      while (w--) {
        const $word = words[w];
        $word.replaceWith($word.textContent);
      }
      words.length = 0;
    }
    if (this.accessible && (canSplitLines || !isCached)) {
      const $accessible = doc.createElement("span");
      $accessible.style.cssText = `position:absolute;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);width:1px;height:1px;white-space:nowrap;`;
      $accessible.innerHTML = this.html;
      $el.insertBefore($accessible, $el.firstChild);
      this.lines.forEach(setAriaHidden);
      this.words.forEach(setAriaHidden);
      this.chars.forEach(setAriaHidden);
    }
    this.width = /** @type {HTMLElement} */
    $el.offsetWidth;
    if (canSplitLines || clearCache) {
      this.effects.forEach((effect, i) => this.effectsCleanups[i] = effect(this));
    }
    return this;
  }
  refresh() {
    this.split(true);
  }
}
const splitText = (target, parameters) => new TextSplitter(target, parameters);
/**
 * Anime.js - utils - ESM
 * @version v4.2.2
 * @license MIT
 * @copyright 2025 - Julian Garnier
 */
const random = (min = 0, max2 = 1, decimalLength = 0) => {
  const m = 10 ** decimalLength;
  return Math.floor((Math.random() * (max2 - min + 1 / m) + min) * m) / m;
};
const shuffle = (items) => {
  let m = items.length, t, i;
  while (m) {
    i = random(0, --m);
    t = items[m];
    items[m] = items[i];
    items[i] = t;
  }
  return items;
};
/**
 * Anime.js - timeline - ESM
 * @version v4.2.2
 * @license MIT
 * @copyright 2025 - Julian Garnier
 */
const getPrevChildOffset = (timeline, timePosition) => {
  if (stringStartsWith(timePosition, "<")) {
    const goToPrevAnimationOffset = timePosition[1] === "<";
    const prevAnimation = (
      /** @type {Tickable} */
      timeline._tail
    );
    const prevOffset = prevAnimation ? prevAnimation._offset + prevAnimation._delay : 0;
    return goToPrevAnimationOffset ? prevOffset : prevOffset + prevAnimation.duration;
  }
};
const parseTimelinePosition = (timeline, timePosition) => {
  let tlDuration = timeline.iterationDuration;
  if (tlDuration === minValue) tlDuration = 0;
  if (isUnd(timePosition)) return tlDuration;
  if (isNum(+timePosition)) return +timePosition;
  const timePosStr = (
    /** @type {String} */
    timePosition
  );
  const tlLabels = timeline ? timeline.labels : null;
  const hasLabels = !isNil(tlLabels);
  const prevOffset = getPrevChildOffset(timeline, timePosStr);
  const hasSibling = !isUnd(prevOffset);
  const matchedRelativeOperator = relativeValuesExecRgx.exec(timePosStr);
  if (matchedRelativeOperator) {
    const fullOperator = matchedRelativeOperator[0];
    const split = timePosStr.split(fullOperator);
    const labelOffset = hasLabels && split[0] ? tlLabels[split[0]] : tlDuration;
    const parsedOffset = hasSibling ? prevOffset : hasLabels ? labelOffset : tlDuration;
    const parsedNumericalOffset = +split[1];
    return getRelativeValue(parsedOffset, parsedNumericalOffset, fullOperator[0]);
  } else {
    return hasSibling ? prevOffset : hasLabels ? !isUnd(tlLabels[timePosStr]) ? tlLabels[timePosStr] : tlDuration : tlDuration;
  }
};
/**
 * Anime.js - utils - ESM
 * @version v4.2.2
 * @license MIT
 * @copyright 2025 - Julian Garnier
 */
const stagger = (val, params = {}) => {
  let values = [];
  let maxValue = 0;
  const from = params.from;
  const reversed = params.reversed;
  const ease = params.ease;
  const hasEasing = !isUnd(ease);
  const hasSpring = hasEasing && !isUnd(
    /** @type {Spring} */
    ease.ease
  );
  const staggerEase = hasSpring ? (
    /** @type {Spring} */
    ease.ease
  ) : hasEasing ? parseEase(ease) : null;
  const grid = params.grid;
  const axis = params.axis;
  const customTotal = params.total;
  const fromFirst = isUnd(from) || from === 0 || from === "first";
  const fromCenter = from === "center";
  const fromLast = from === "last";
  const fromRandom = from === "random";
  const isRange = isArr(val);
  const useProp = params.use;
  const val1 = isRange ? parseNumber(val[0]) : parseNumber(val);
  const val2 = isRange ? parseNumber(val[1]) : 0;
  const unitMatch = unitsExecRgx.exec((isRange ? val[1] : val) + emptyString);
  const start = params.start || 0 + (isRange ? val1 : 0);
  let fromIndex = fromFirst ? 0 : isNum(from) ? from : 0;
  return (target, i, t, tl) => {
    const [registeredTarget] = registerTargets(target);
    const total = isUnd(customTotal) ? t : customTotal;
    const customIndex = !isUnd(useProp) ? isFnc(useProp) ? useProp(registeredTarget, i, total) : getOriginalAnimatableValue(registeredTarget, useProp) : false;
    const staggerIndex = isNum(customIndex) || isStr(customIndex) && isNum(+customIndex) ? +customIndex : i;
    if (fromCenter) fromIndex = (total - 1) / 2;
    if (fromLast) fromIndex = total - 1;
    if (!values.length) {
      for (let index = 0; index < total; index++) {
        if (!grid) {
          values.push(abs(fromIndex - index));
        } else {
          const fromX = !fromCenter ? fromIndex % grid[0] : (grid[0] - 1) / 2;
          const fromY = !fromCenter ? floor(fromIndex / grid[0]) : (grid[1] - 1) / 2;
          const toX = index % grid[0];
          const toY = floor(index / grid[0]);
          const distanceX = fromX - toX;
          const distanceY = fromY - toY;
          let value = sqrt(distanceX * distanceX + distanceY * distanceY);
          if (axis === "x") value = -distanceX;
          if (axis === "y") value = -distanceY;
          values.push(value);
        }
        maxValue = max(...values);
      }
      if (staggerEase) values = values.map((val3) => staggerEase(val3 / maxValue) * maxValue);
      if (reversed) values = values.map((val3) => axis ? val3 < 0 ? val3 * -1 : -val3 : abs(maxValue - val3));
      if (fromRandom) values = shuffle(values);
    }
    const spacing = isRange ? (val2 - val1) / maxValue : val1;
    const offset = tl ? parseTimelinePosition(tl, isUnd(params.start) ? tl.iterationDuration : start) : (
      /** @type {Number} */
      start
    );
    let output = offset + (spacing * round(values[staggerIndex], 2) || 0);
    if (params.modifier) output = params.modifier(output);
    if (unitMatch) output = `${output}${unitMatch[2]}`;
    return output;
  };
};
const ANIM_PRESETS = {
  blur_reveal: {
    filter: ["blur(.5rem)", "blur(0rem)"],
    opacity: [0, 1],
    y: ["-.5rem", "0rem"],
    duration: 1e3,
    delay: stagger(100)
  },
  fade_in: {
    opacity: [0.4, 1],
    duration: 1e3,
    delay: stagger(400)
  },
  scale_reveal: {
    scale: [1.8, 1],
    opacity: [0, 1],
    duration: 1e3,
    delay: stagger(400)
  },
  random_stagger: {
    opacity: [0, 1],
    duration: 1e3,
    delay: stagger(50, { from: "random" })
  },
  random_scale: {
    filter: ["blur(1rem)", "blur(0rem)"],
    scale: [2, 1],
    opacity: [0, 1],
    duration: 1e3,
    delay: stagger(200, { from: "random" })
  },
  overscale: {
    scale: [3, 1],
    opacity: [0, 1],
    x: ["1em", "0rem"],
    duration: 1e3,
    delay: stagger(50)
  },
  slide: {
    x: ["-2ch", "0"],
    opacity: [0, 1],
    duration: 1e3,
    delay: stagger(20)
  }
};
const WORD_LEVEL_TYPES = /* @__PURE__ */ new Set(["scale_reveal", "random_scale", "blur_reveal"]);
class HighlightTextWImg extends HTMLElement {
  connectedCallback() {
    this._type = this.dataset.animationType;
    this._noReplay = this.dataset.animationDisableReplay === "true";
    this._contentBox = this.querySelector(".htwi-content-text-block-inline");
    this._anim = null;
    this._split = null;
    this.classList.add("active");
    const init = () => {
      this._init();
      this._setupResizeObserver();
    };
    if (this.dataset.sectionIndex === "1") {
      init();
    } else {
      lazyInit(this, init);
    }
  }
  disconnectedCallback() {
    var _a;
    this._cleanup();
    (_a = this._ro) == null ? void 0 : _a.disconnect();
    this._ro = null;
  }
  _init() {
    this._split = splitText(this._contentBox, {
      chars: {
        wrap: this._type === "slide",
        class: "char"
      },
      words: { class: "word" }
    });
    const selector = WORD_LEVEL_TYPES.has(this._type) ? ".word, .htwi-inline-img-inline" : ".char, .htwi-inline-img-inline";
    const targets = this.querySelectorAll(selector);
    const wcValue = this._type === "slide" ? "transform, opacity" : "transform, opacity, filter";
    targets.forEach((el) => {
      el.style.willChange = wcValue;
    });
    this._split.addEffect(() => {
      this._anim = waapi.animate(targets, {
        ...ANIM_PRESETS[this._type],
        ease: "linear",
        composition: "replace",
        autoplay: onScroll({
          target: this._contentBox,
          enter: "center top-=35vh",
          leave: "center bottom+=15vh",
          sync: true,
          onLeave: (self) => {
            if (this._noReplay && self.progress === 1) {
              self.revert();
              this._anim = null;
              targets.forEach((el) => {
                el.style.willChange = "";
              });
            }
          }
        })
      });
      return this._anim;
    });
  }
  _cleanup() {
    var _a;
    this._contentBox = null;
    if (this._anim) {
      this._anim.revert();
      (_a = this._anim.autoplay) == null ? void 0 : _a.revert();
      this._anim = null;
    }
    if (this._split) {
      this._split.revert();
      this._split = null;
    }
    this.querySelectorAll(".word, .char, .htwi-inline-img-inline").forEach((el) => {
      el.style.willChange = "";
    });
  }
  _setupResizeObserver() {
    const onResize = debounce(() => {
      if (!this._contentBox) return;
      if (!this.isConnected) return;
      this._cleanup();
      const contentBox = this.querySelector(".htwi-content-text-block-inline");
      if (!contentBox || !contentBox.isConnected) return;
      this._contentBox = contentBox;
      this._init();
    }, 200);
    this._ro = new ResizeNotifier(onResize);
    this._ro.observe(this);
  }
}
if (!customElements.get("highlight-text-w-img-component")) {
  customElements.define("highlight-text-w-img-component", HighlightTextWImg);
}
